/**
 * RoutineManagerPanel — management-at-scale surface for Routine Threads
 * (2026-07-31). Opened from the sidebar's "Routines · Manage" button.
 *
 * A body-portal overlay (BODY_PORTAL_Z_INDEX — fullscreen burial lesson, see
 * styles.ts) listing EVERY routine on the account, from two sources unioned
 * by threadId (a routine's identity IS its thread — one per thread):
 *   • LIVE rows — threads with `routineId` from the existing thread-index
 *     subscription, so pause/resume/cancel results stream back in through the
 *     same mirror the sidebar uses. Ordered by `orderThreads` — THE shared
 *     display ordering (pinned first, then activity) every thread renderer
 *     uses, so the panel can never disagree with the sidebar.
 *   • SERVER-ONLY rows — from the SHARED `useAccountRoutines` snapshot
 *     (routine list parity, 2026-08-02 — the fetch/state moved OUT of this
 *     panel into the hook the sidebar hosts, so the rail section and this
 *     panel consume the SAME `listRoutines()` result and can never disagree
 *     about membership or count). The union itself is the shared pure
 *     builder in routineRows.ts. The panel still refreshes ON OPEN
 *     (fresh-on-open preserved; the hook dedupes concurrent fetches). While
 *     a fetch is in flight a quiet notice shows; on failure the panel still
 *     renders the live rows plus an inline degraded-list warning.
 * Per-row actions:
 *   • Open           → switchThread (host callback) + close the panel.
 *     VERIFIED safe for server-only rows (useChatThreads.switchThread): an id
 *     absent from local state loads its transcript by threadId
 *     (localStorage, else hydrateThreadMessagesFromServerIfEmpty); since
 *     2026-08-02 the switch also hydrates the missing LOCAL RECORD from the
 *     single index doc (getChatThread) — server-only rows pass the routine
 *     name as the fallback title for the missing/archived-doc case.
 *     REGRESSION FIX (production 2026-08-01): the overlay MUST carry
 *     `data-prest0n-portal="true"`. This panel body-portals outside the chat
 *     container, and without the marker useGlobalChat's document-level
 *     mousedown handleClickOutside classified a click INSIDE the panel as
 *     "outside the chat" and closed the whole chat pane before the click
 *     completed — so Open never navigated (the exact O2 guest sign-in bug
 *     class; see portalClickGuard.ts).
 *   • Pause / Resume → POST prest0nRoutineAction via services/routines/client
 *   • Stop           → inline confirm ("history stays — only the routine
 *                      stops"), then cancel via the same client
 * STATUS TRUTH (production drift fix 2026-08-01 — the manager showed "Pause"
 * + "Next in 3d" for a PAUSED routine): a live row's status comes from the
 * thread-index MIRROR, which is best-effort twice over — the server's
 * applyRoutineMirror write is fire-and-forget, and the capped index
 * subscription stops refreshing a thread once it ages out of the
 * most-recently-active window (paused threads stop advancing lastActiveAt;
 * the ACTIVE thread is then kept locally with a FROZEN mirror). The
 * listRoutines() rows, by contrast, are built from the ROUTINE DOC itself —
 * authoritative. So the union still lets live rows win for display fields,
 * but on a STATUS conflict the server list's status (+ its nextRunAt, which
 * travels with it) wins; in-panel actions keep the fetched list current, so
 * the rule can't fight the user's own clicks. On success the panel patches
 * the fetched server list (hook patchRoutine) AND (via onRoutineMirrorPatch)
 * the host's local thread record, so the sidebar/header flip with the
 * manager instead of waiting on an echo that may never come — and THEN
 * refreshes the shared list so both surfaces converge on server truth.
 * (The authoritative-snapshot drift HEAL of host records lives in
 * useAccountRoutines now — it runs on every successful fetch, not just
 * panel opens.)
 * Buttons disable while their row's action is in flight; failures surface as
 * inline row errors. Schedule EDITS are conversational in the routine thread
 * by design — this panel deliberately has no edit affordance.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ChatThread } from './types';
import { RoutineGlyph, routineNextRunLabel, routinePastTimeLabel } from './RoutineGlyph';
import type { ThreadRoutineMirrorPatch } from './useChatThreads';
import { buildRoutineManagerRows, msToIso } from './routineRows';
import type { AccountRoutinesApi } from './useAccountRoutines';
import {
  RoutineManagerOverlay,
  RoutineManagerPanelContainer,
  RoutineManagerHeader,
  RoutineManagerTitle,
  RoutineManagerCloseButton,
  RoutineManagerList,
  RoutineManagerListNotice,
  RoutineManagerRow,
  RoutineManagerRowTitleLine,
  RoutineManagerRowName,
  RoutineManagerRowThreadTitle,
  RoutineManagerRowMeta,
  RoutineManagerActions,
  RoutineManagerActionButton,
  RoutineManagerError,
  RoutineManagerConfirm,
  RoutineManagerEmptyState,
  RoutinePausedChip,
  RoutineStatusDot,
} from './Routines.styles';
import {
  pauseRoutine,
  resumeRoutine,
  cancelRoutine,
  type RoutineActionResult,
  type RoutineSummary,
} from '../../services/routines/client';

// Back-compat re-exports (the builder moved to routineRows.ts, 2026-08-02 —
// shared with ThreadSidebar's routines section; see that module's doc).
export { buildRoutineManagerRows } from './routineRows';
export type { RoutineManagerRowView } from './routineRows';

export interface RoutineManagerPanelProps {
  /** The live thread list (the panel filters to threads with routineId). */
  threads: ChatThread[];
  /**
   * The SHARED account-routines state (routine list parity, 2026-08-02) —
   * the exact `useAccountRoutines` instance the sidebar's routines section
   * consumes, passed down so the two surfaces render one snapshot.
   */
  accountRoutines: AccountRoutinesApi;
  /**
   * Open a routine's thread (host switchThread); the panel closes itself.
   * Server-only rows pass `{ fallbackTitle: routine name }` so navigation
   * hydration can label the destination if its index doc is missing.
   */
  onOpenThread: (threadId: string, options?: { fallbackTitle?: string }) => void;
  /** Close the panel (overlay click, ×, Escape). */
  onClose: () => void;
  /**
   * Patch the host's LOCAL thread record's routine mirror
   * (threadStore.patchThreadRoutineMirror — local-only, subscription
   * reconciles on top). Called after a successful pause/resume so the
   * sidebar row + header flip with the manager. (The authoritative-snapshot
   * drift heal moved into useAccountRoutines.) Optional — without it the
   * panel still renders correct rows via the union's status rule.
   */
  onRoutineMirrorPatch?: (threadId: string, patch: ThreadRoutineMirrorPatch) => void;
}

export const RoutineManagerPanel: React.FC<RoutineManagerPanelProps> = ({
  threads,
  accountRoutines,
  onOpenThread,
  onClose,
  onRoutineMirrorPatch,
}) => {
  const [inFlightIds, setInFlightIds] = useState<Set<string>>(new Set());
  const [errorsById, setErrorsById] = useState<Record<string, string>>({});
  const [confirmStopId, setConfirmStopId] = useState<string | null>(null);
  const { routines, fetchState, refresh, patchRoutine, removeRoutine } = accountRoutines;

  const onRoutineMirrorPatchRef = useRef(onRoutineMirrorPatch);
  onRoutineMirrorPatchRef.current = onRoutineMirrorPatch;

  // FRESH-ON-OPEN (preserved from the original in-panel fetch): the panel
  // mounts on open, so one ref-guarded refresh per mount keeps the shared
  // snapshot current.
  //
  // IN-FLIGHT AWARE (adversarial review finding 1 follow-through, 2026-08-03):
  // the hook's refresh() now schedules a TRAILING run for any call made during
  // a flight — it cannot know whether the caller's state change made it into
  // the in-flight request, so it must assume it did not. That is required for
  // the archive-parity trigger, but it would make "hook mount + manager open
  // in the same commit" cost TWO Cloud Function invocations. It doesn't need
  // to: a fetch that is already in flight is about to land fresh data, which
  // is exactly what fresh-on-open wants. So skip while `loading`, and refresh
  // whenever the shared snapshot has settled ('done') or failed ('error' —
  // opening the manager is a natural retry point).
  const fetchStateRef = useRef(fetchState);
  fetchStateRef.current = fetchState;
  const refreshedOnOpenRef = useRef(false);
  useEffect(() => {
    if (refreshedOnOpenRef.current) return;
    refreshedOnOpenRef.current = true;
    if (fetchStateRef.current === 'loading') return;
    void refresh();
  }, [refresh]);

  // Escape closes — document-level because the panel lives in a body portal.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  /**
   * Post-action host patch (2026-08-01): flip the host's local thread record
   * alongside the fetched-list patch so the sidebar/header agree with this
   * panel immediately — the subscription echo is best-effort and may never
   * arrive for threads outside the live index window. Prefers the response
   * echo's post-action truth when present. No-op for server-only rows (the
   * host patch guards on a local routineId).
   */
  const patchHostThreadStatus = useCallback((
    threadId: string,
    status: RoutineSummary['status'],
    routine?: RoutineActionResult['routine'],
  ) => {
    const patch = onRoutineMirrorPatchRef.current;
    if (!patch) return;
    const fresh = routine && routine.threadId === threadId ? routine : null;
    patch(threadId, {
      routineStatus: fresh?.status ? fresh.status : status,
      routineNextRunAt: fresh
        ? msToIso(fresh.nextRunAtMs)
        : (status === 'paused' ? null : undefined),
      ...(fresh?.name ? { routineName: fresh.name } : {}),
      ...(fresh?.scheduleSummary ? { routineScheduleSummary: fresh.scheduleSummary } : {}),
    });
  }, []);

  const runAction = useCallback(async (
    routineId: string,
    action: (id: string) => Promise<RoutineActionResult>,
    failureLabel: string,
    onSuccess?: (result: RoutineActionResult) => void,
  ) => {
    setInFlightIds((prev) => new Set(prev).add(routineId));
    setErrorsById((prev) => {
      if (!(routineId in prev)) return prev;
      const next = { ...prev };
      delete next[routineId];
      return next;
    });
    try {
      const result = await action(routineId);
      // No LIVE-row mutation on success: the server updates the thread-index
      // mirror and the live subscription streams the new state into
      // `threads`. onSuccess patches the shared fetched list + host record,
      // then the shared list refreshes for server truth (parity trigger).
      if (onSuccess) onSuccess(result);
      void refresh();
    } catch {
      setErrorsById((prev) => ({ ...prev, [routineId]: failureLabel }));
    } finally {
      setInFlightIds((prev) => {
        const next = new Set(prev);
        next.delete(routineId);
        return next;
      });
    }
  }, [refresh]);

  const rows = buildRoutineManagerRows(threads, routines);
  // Host-mirror patch guard (2026-08-16 review): the thread mirror is an
  // AGGREGATE — flipping it from ONE routine's post-action status is only
  // truthful when that routine is the thread's ONLY one. Multi-routine
  // threads skip the optimistic host patch and let the post-action refresh's
  // aggregate drift heal (useAccountRoutines) settle the mirror.
  const routineCountByThread = new Map<string, number>();
  for (const row of rows) {
    routineCountByThread.set(row.threadId, (routineCountByThread.get(row.threadId) || 0) + 1);
  }

  const panel = (
    <RoutineManagerOverlay
      data-routine-manager-overlay=""
      // OPEN-REGRESSION FIX (production 2026-08-01): mark this body portal as
      // a chat surface. Without it, useGlobalChat's document-mousedown
      // outside-click handler closed the whole chat pane on ANY click inside
      // the panel — unmounting the Open button before its click could fire,
      // so Open never navigated. Shared predicate: portalClickGuard.ts (the
      // O2 lesson); ThreadAddPeoplePanel wears the same marker.
      data-prest0n-portal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <RoutineManagerPanelContainer
        role="dialog"
        aria-modal="true"
        aria-label="Routines"
        data-routine-manager-panel=""
      >
        <RoutineManagerHeader>
          <RoutineManagerTitle>
            <RoutineGlyph size={14} />
            Routines
          </RoutineManagerTitle>
          <RoutineManagerCloseButton
            type="button"
            data-routine-manager-close=""
            aria-label="close routine manager"
            title="close"
            onClick={onClose}
          >
            ×
          </RoutineManagerCloseButton>
        </RoutineManagerHeader>

        {fetchState === 'loading' && (
          <RoutineManagerListNotice data-routine-manager-list-loading="">
            Checking for more routines…
          </RoutineManagerListNotice>
        )}
        {fetchState === 'error' && (
          <RoutineManagerListNotice
            $tone="error"
            role="alert"
            data-routine-manager-list-error=""
          >
            Couldn&rsquo;t load the full routine list — showing recently
            active routines only.
          </RoutineManagerListNotice>
        )}

        {rows.length === 0 ? (
          // Only claim "no routines yet" once the full list has confirmed it —
          // while loading (or after a failed fetch) absence proves nothing.
          fetchState === 'done' && (
            <RoutineManagerEmptyState data-routine-manager-empty="">
              No routines yet. A routine is automatic work Prest0n runs for you
              — on a schedule or whenever something happens (a new email, a
              settings change, a spend) — with every run posted into its
              thread. To create one, just ask in any chat — e.g. &ldquo;Every
              weekday at 8am, summarize my inbox&rdquo; or &ldquo;Every time a
              new email comes in, flag anything urgent&rdquo;.
            </RoutineManagerEmptyState>
          )
        ) : (
          <RoutineManagerList>
            {rows.map((row) => {
              const inFlight = inFlightIds.has(row.routineId);
              const errorText = errorsById[row.routineId];
              const confirmingStop = confirmStopId === row.routineId;
              // v2: trigger routines have no clock — the cadence text IS the
              // trigger summary, already rendered from scheduleSummary.
              // P2: a routine whose dates are spent is COMPLETE, not paused —
              // and it gets no Resume button, because resume refuses.
              const nextRun = row.completed
                ? 'Completed'
                : row.paused
                ? 'Paused'
                : (row.kind === 'trigger' ? 'Runs on its trigger' : routineNextRunLabel(row.nextRunAt));
              const lastRunTime = routinePastTimeLabel(row.lastRunAt);
              return (
                <RoutineManagerRow
                  key={row.routineId}
                  data-routine-manager-row={row.routineId}
                  data-routine-manager-row-thread={row.threadId}
                  data-routine-manager-row-source={row.source}
                >
                  <RoutineManagerRowTitleLine>
                    <RoutineGlyph active={!row.paused} size={13} />
                    <RoutineManagerRowName>
                      {row.name}
                    </RoutineManagerRowName>
                    {row.threadTitle && (
                      <RoutineManagerRowThreadTitle
                        data-routine-row-thread-title=""
                        title={row.threadTitle}
                      >
                        {row.threadTitle}
                      </RoutineManagerRowThreadTitle>
                    )}
                    {row.paused && (
                      <RoutinePausedChip data-routine-paused-chip={row.completed ? undefined : ''} data-routine-completed-chip={row.completed ? '' : undefined}>
                        {row.completed ? 'Completed' : 'Paused'}
                      </RoutinePausedChip>
                    )}
                  </RoutineManagerRowTitleLine>
                  <RoutineManagerRowMeta data-routine-manager-row-meta="">
                    {row.scheduleSummary && <span>{row.scheduleSummary}</span>}
                    {nextRun && <span>{nextRun}</span>}
                    {row.lastRunStatus && (
                      <span
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        data-routine-last-run={row.lastRunStatus}
                      >
                        <RoutineStatusDot
                          $status={row.lastRunStatus}
                          aria-hidden="true"
                        />
                        {`Last run${row.lastRunStatus === 'error' ? ' failed' : ''}${lastRunTime ? ` ${lastRunTime}` : ''}`}
                      </span>
                    )}
                  </RoutineManagerRowMeta>
                  {confirmingStop ? (
                    <RoutineManagerConfirm data-routine-stop-confirm="">
                      Stop this routine? The thread, its history, and any other
                      routines it holds stay — only this routine stops.
                      <RoutineManagerActionButton
                        type="button"
                        $variant="danger"
                        data-routine-stop-confirm-btn=""
                        disabled={inFlight}
                        onClick={() => {
                          setConfirmStopId(null);
                          void runAction(
                            row.routineId,
                            cancelRoutine,
                            'Could not stop this routine — please try again.',
                            () => removeRoutine(row.routineId),
                          );
                        }}
                      >
                        Stop routine
                      </RoutineManagerActionButton>
                      <RoutineManagerActionButton
                        type="button"
                        data-routine-stop-keep-btn=""
                        disabled={inFlight}
                        onClick={() => setConfirmStopId(null)}
                      >
                        Keep
                      </RoutineManagerActionButton>
                    </RoutineManagerConfirm>
                  ) : (
                    <RoutineManagerActions>
                      <RoutineManagerActionButton
                        type="button"
                        data-routine-open-btn=""
                        disabled={inFlight}
                        onClick={() => {
                          // Server-only rows carry the routine name into the
                          // navigation hydration fallback (missing index doc).
                          if (row.source === 'server') {
                            onOpenThread(row.threadId, { fallbackTitle: row.name });
                          } else {
                            onOpenThread(row.threadId);
                          }
                          onClose();
                        }}
                      >
                        Open
                      </RoutineManagerActionButton>
                      {row.completed ? null : row.paused ? (
                        <RoutineManagerActionButton
                          type="button"
                          data-routine-resume-btn=""
                          disabled={inFlight}
                          onClick={() => {
                            void runAction(
                              row.routineId,
                              resumeRoutine,
                              'Could not resume this routine — please try again.',
                              (result) => {
                                patchRoutine(row.routineId, 'active', result.routine);
                                if ((routineCountByThread.get(row.threadId) ?? 1) === 1) {
                                  patchHostThreadStatus(row.threadId, 'active', result.routine);
                                }
                              },
                            );
                          }}
                        >
                          Resume
                        </RoutineManagerActionButton>
                      ) : (
                        <RoutineManagerActionButton
                          type="button"
                          data-routine-pause-btn=""
                          disabled={inFlight}
                          onClick={() => {
                            void runAction(
                              row.routineId,
                              pauseRoutine,
                              'Could not pause this routine — please try again.',
                              (result) => {
                                patchRoutine(row.routineId, 'paused', result.routine);
                                if ((routineCountByThread.get(row.threadId) ?? 1) === 1) {
                                  patchHostThreadStatus(row.threadId, 'paused', result.routine);
                                }
                              },
                            );
                          }}
                        >
                          Pause
                        </RoutineManagerActionButton>
                      )}
                      <RoutineManagerActionButton
                        type="button"
                        $variant="danger"
                        data-routine-stop-btn=""
                        disabled={inFlight}
                        onClick={() => setConfirmStopId(row.routineId)}
                      >
                        Stop
                      </RoutineManagerActionButton>
                    </RoutineManagerActions>
                  )}
                  {errorText && (
                    <RoutineManagerError role="alert" data-routine-manager-error="">
                      {errorText}
                    </RoutineManagerError>
                  )}
                </RoutineManagerRow>
              );
            })}
          </RoutineManagerList>
        )}
      </RoutineManagerPanelContainer>
    </RoutineManagerOverlay>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(panel, document.body);
};

export default RoutineManagerPanel;
